// frontend/src/pages/Create.jsx
// ✅ UPDATED: Added token expiry handling
import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Screen from "../uizard/ui/Screen";
import { ACTIVE_STYLES } from "../shared/styles";
import { getIdentityHeaders, waitForIdentity, refreshTokenIfNeeded } from '../lib/identityGoogle';
import { API_BASE, toast } from "../lib/api";

const api = (path) => (API_BASE ? `${API_BASE}${path}` : path);

function normKey(s){ return String(s?.key ?? s?.slug ?? "").trim().toLowerCase(); }
function firstImg(s){
  if (Array.isArray(s?.examples) && s.examples[0]) return s.examples[0];
  if (s?.img) return s.img;
  const k = normKey(s);
  return k ? `/assets/styles/${k}.jpg` : "";
}
const STYLES = ACTIVE_STYLES.map((s) => ({
  key: normKey(s), 
  name: s?.name || "", 
  hint: s?.tagline || s?.hint || "",
  img: firstImg(s), 
  badge: s?.badge || "",
  status: s?.status || "ready",
}));
const RATIOS = ["1:1", "16:9", "9:16", "4:3", "3:4"];

export default function Create() {
  const nav = useNavigate();
  const location = useLocation();

  const [prompt, setPrompt] = React.useState("");
  const [style, setStyle] = React.useState("");
  const [ratio, setRatio] = React.useState("1:1");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [credits, setCredits] = React.useState(null);
  const [styleStats, setStyleStats] = React.useState({});

  // Fetch style stats
  React.useEffect(() => {
    const fetchStyleStats = async () => {
      try {
        await waitForIdentity();
        const res = await fetch(api("/api/styles/stats"), {
          credentials: "include",
          headers: { ...getIdentityHeaders() },
        });
        if (!res.ok) return;
        const data = await res.json();
        setStyleStats(data.styles || {});
      } catch (e) {
        console.warn("Failed to fetch style stats:", e);
      }
    };
    fetchStyleStats();
  }, []);

  // Seed from Result handoff
  React.useEffect(() => {
    const s = location.state && location.state.fromResult;
    if (!s) return;
    if (s.prompt) setPrompt(String(s.prompt));
    if (s.style) setStyle(String(s.style).trim().toLowerCase());
    if (s.aspectRatio) setRatio(String(s.aspectRatio));
  }, [location.state]);

  // Seed from query params
  React.useEffect(() => {
    const qs = new URLSearchParams(location.search || "");
    const qStyle = qs.get("style");
    const qPrompt = qs.get("prompt");
    if (qStyle) setStyle(String(qStyle).trim().toLowerCase());
    if (qPrompt) setPrompt(String(qPrompt));
  }, [location.search]);

  // Fetch credits
  const refreshCredits = React.useCallback(async () => {
    try {
      await waitForIdentity();
      const res = await fetch(api("/api/credits"), {
        credentials: "include",
        headers: { ...getIdentityHeaders() },
      });
      if (!res.ok) return;
      const j = await res.json();
      if (typeof j.credits === "number") setCredits(j.credits);
    } catch (e) {
      console.warn("Failed to fetch credits:", e);
    }
  }, []);

  // Initial fetch
  React.useEffect(() => { (async () => { await refreshCredits(); })(); }, [refreshCredits]);

  // Keep in sync
  React.useEffect(() => {
    const onFocus = () => refreshCredits();
    const onVisibility = () => { if (document.visibilityState === "visible") refreshCredits(); };
    const onStorage = (e) => {
      if (e.key === "ab:credits:ping") refreshCredits();
    };
    const onCustom = () => refreshCredits();

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("storage", onStorage);
    window.addEventListener("ab:credits", onCustom);

    const id = setInterval(refreshCredits, 15000);

    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("ab:credits", onCustom);
      clearInterval(id);
    };
  }, [refreshCredits]);

  const disabled = React.useMemo(
    () => loading || !prompt.trim() || (typeof credits === "number" && credits <= 0),
    [loading, prompt, credits]
  );

  // ✅ UPDATED: Generate with token expiry handling
  const handleGenerate = React.useCallback(async () => {
    if (disabled) return;
    
    // ✅ Check token expiry FIRST
    const tokenValid = await refreshTokenIfNeeded();
    if (!tokenValid) {
      setError("Your session has expired. Please sign in again.");
      toast("Session expired - please sign in");
      setTimeout(() => {
        window.location.href = "/landing";
      }, 1500);
      return;
    }
    
    setError("");
    setLoading(true);
    try {
      const body = { prompt: prompt.trim(), aspectRatio: ratio, ...(style ? { style } : {}) };
      const res = await fetch(api("/api/generations"), {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getIdentityHeaders() },
        credentials: "include",
        body: JSON.stringify(body),
      });
      
      // ✅ Handle 401 from backend
      if (res.status === 401) {
        localStorage.clear();
        setError("Session expired. Redirecting to login...");
        toast("Session expired - please sign in");
        setTimeout(() => {
          window.location.href = '/landing';
        }, 1500);
        return;
      }
      
      const j = await res.json();
      if (!res.ok) throw new Error(j?.error || res.statusText);

      if (typeof j.credits === "number") setCredits(j.credits); else await refreshCredits();

      try {
        localStorage.setItem("ab:credits:ping", String(Date.now()));
        window.dispatchEvent(new Event("ab:credits"));
      } catch {}

      toast("Generating…");
      nav(`/result/${j.id}`);
    } catch (e) {
      setError(e.message || "Failed to generate");
      toast(e.message || "Generation failed");
    } finally {
      setLoading(false);
    }
  }, [disabled, prompt, style, ratio, nav, refreshCredits]);

  // Ctrl/⌘ + Enter
  React.useEffect(() => {
    function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        handleGenerate();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleGenerate]);

  const charCount = prompt.length;
  const maxChars = 800;

  const selectedStyleObj = React.useMemo(() => {
    if (!style) return null;
    
    const staticStyle = STYLES.find((s) => s.key === style);
    const stats = styleStats[style] || {};
    
    const base = staticStyle || { key: style, name: style, hint: "", img: "", badge: "", status: "ready" };
    
    return {
      ...base,
      hasArtist: Boolean(stats.artist),
      usageCount: stats.usageCount || 0,
      reputation: stats.reputation || null,
    };
  }, [style, styleStats]);

  return (
    <Screen>
      <StyleTag />
      <div className="create-wrap">
        <h1
          className="h1"
          style={{
            background: "var(--ab-hero-grad, linear-gradient(135deg, #1E3A8A 0%, #6D28D9 100%))",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            color: "transparent",
          }}
        >
          Describe your image
        </h1>

        <div className="grid">
          <section className="panel">
            <label className="label">Prompt</label>

            <div className="textarea-wrap">
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value.slice(0, maxChars))}
                placeholder="e.g. A neon alley at night, puddle reflections, cinematic, 35mm, soft fog"
              />
              <div className="counter">{charCount}/{maxChars}</div>
            </div>

            <p className="subtle">Tip: detailed but concise prompts work best.</p>

            <div className="row">
              <div className="col">
                <label className="label">Style</label>
                <select className="select" value={style} onChange={(e) => setStyle(e.target.value)}>
                  <option value="">None (Default)</option>
                  {ACTIVE_STYLES.map((s) => (
  <option key={s.key} value={s.key}>
    {s.name}
  </option>
))}
                </select>
              </div>
              <div className="col col-sm">
                <label className="label">Aspect ratio</label>
                <select className="select" value={ratio} onChange={(e) => setRatio(e.target.value)}>
                  {RATIOS.map((r) => (
                    <option key={r} value={r}>
                      {r === "16:9" || r === "9:16" ? `${r} — best for animation` : r}
                    </option>
                  ))}
                </select>
                {ratio !== "16:9" && ratio !== "9:16" && (
                  <div className="ratio-hint">
                    Tip: 16:9 or 9:16 animate best later.
                  </div>
                )}
              </div>
            </div>

            {error && <div className="error">{error}</div>}

            <div className="cta">
              <button className="btn btn-primary" onClick={handleGenerate} disabled={disabled}>
                {loading ? "Generating…" : "Generate"}
              </button>
              <div className="hint">
                1 credit per generation
                {typeof credits === "number" ? (
                  <span style={{ marginLeft: 8, color: "#6B7280" }}>
                    • Available: {credits}
                  </span>
                ) : null}
              </div>
            </div>
          </section>

          <aside className="panel preview">
            <div className="preview-title">Style preview</div>
            <div className="preview-body">
              <PreviewBox styleObj={selectedStyleObj} />
            </div>
            
            <CreatorCallout />
          </aside>
        </div>
      </div>
    </Screen>
  );
}

function PreviewBox({ styleObj }) {
  const [broken, setBroken] = React.useState(false);
  
  if (!styleObj) {
    return (
      <>
        <div className="preview-box placeholder-box">
          <img src="/placeholder.png" alt="Style Preview" className="placeholder-img" />
        </div>
      </>
    );
  }

  const hasArtist = styleObj.hasArtist;
  const usageCount = styleObj.usageCount || 0;
  const reputation = styleObj.reputation;

  return (
    <>
      <div className="preview-box with-style">
        {styleObj.img && !broken ? (
          <img src={styleObj.img} alt={styleObj.name} onError={() => setBroken(true)} />
        ) : (
          <div className="preview-gradient" />
        )}
        <div className="preview-badge-wrapper">
          <div className="preview-badge">{styleObj.name}</div>
          {styleObj.status === "in-dev" && (
            <div className="preview-dev-badge" title="This style is still being refined">
              🚧
            </div>
          )}
        </div>
      </div>
      <div className="preview-meta">{styleObj.hint}</div>
      
      {hasArtist && (
        <div className="artist-attribution">
          <div className="artist-attribution-text">
            By using this style you are supporting an <strong>independent artist</strong>
          </div>
          <div className="artist-attribution-stats-row">
            {usageCount > 0 && (
              <div className="artist-attribution-stats">
                Used {usageCount.toLocaleString()} {usageCount === 1 ? "time" : "times"}
              </div>
            )}
            {reputation && (
              <div className="artist-reputation">
                <ReputationBadge reputation={reputation} />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function ReputationBadge({ reputation }) {
  if (!reputation || typeof reputation.score !== 'number') return null;

  const { score, level } = reputation;
  
  const levelConfig = {
    emerging: { label: "Emerging", color: "#10B981", icon: "🌱" },
    established: { label: "Established", color: "#3B82F6", icon: "⭐" },
    verified: { label: "Verified", color: "#8B5CF6", icon: "✓" },
  };

  const config = levelConfig[level] || levelConfig.emerging;

  return (
    <div 
      className="reputation-badge"
      style={{ borderColor: config.color }}
      title={`Reputation: ${score}/100`}
    >
      <span className="reputation-icon">{config.icon}</span>
      <span className="reputation-label" style={{ color: config.color }}>
        {config.label}
      </span>
    </div>
  );
}

function CreatorCallout() {
  const navigate = useNavigate();
  
  return (
    <div className="creator-callout">
      <div className="creator-callout-header">
        <h3 className="creator-callout-title">Become a Creator</h3>
      </div>
      
      <p className="creator-callout-text">
        Create your own AI styles and earn royalties when others use them
      </p>
      
      <div className="creator-callout-benefits">
        <div className="benefit-item">
          <span className="benefit-text">Earn royalties</span>
        </div>
        <div className="benefit-item">
          <span className="benefit-text">Optimize performance</span>
        </div>
        <div className="benefit-item">
          <span className="benefit-text">Build reputation</span>
        </div>
      </div>
      
      <button 
        className="creator-callout-cta"
        onClick={() => navigate('/creator-hub')}
      >
        Join Royalty Program →
      </button>
    </div>
  );
}

function StyleTag() {
  return (
    <style>{`
.create-wrap{ max-width: 1100px; margin: 0 auto; }
.grid{ display:grid; grid-template-columns: 1.15fr 0.85fr; gap:24px; margin-top:10px; }
@media (max-width: 1100px){ .grid{ grid-template-columns:1fr; } }
.panel{ background:#fff; border:1px solid #E5E7EB; border-radius:16px; padding:18px; }
.label{ display:block; font-weight:800; margin-bottom:10px; }
.textarea-wrap{ position:relative; }
.textarea-wrap textarea{
  width:100%; height:220px; border:1px solid #E5E7EB; border-radius:12px;
  padding:12px 14px; resize: vertical; font: inherit; outline:none; line-height: 1.45;
}
.textarea-wrap textarea:focus{ border-color:#6D5EF9; box-shadow:0 0 0 3px #F3F0FF; }
.counter{ position:absolute; right:10px; bottom:8px; font-size:12px; color:#6B7280; }
.subtle{ color:#6B7280; margin-top:8px; margin-bottom:14px; }
.row{ display:flex; gap:16px; align-items:flex-start; flex-wrap:wrap; margin-top:4px; }
.col{ flex:1 1 420px; }
.col-sm{ flex:0 0 220px; }
.select{ width:100%; height:44px; border-radius:12px; border:1px solid #E5E7EB; padding:8px 12px; background:#fff; box-shadow: 0 2px 4px rgba(0,0,0,0.04); transition: all 0.2s ease; cursor: pointer; }
.select:hover{ border-color:#C4B5FD; box-shadow: 0 4px 8px rgba(0,0,0,0.08); }
.select:focus{ outline:none; border-color:#6D5EF9; box-shadow: 0 0 0 3px #F3F0FF; }
.cta{ display:flex; align-items:center; gap:12px; margin-top:14px; }
.btn{ height:44px; padding:0 18px; border-radius:12px; border:1px solid #E5E7EB; background:#fff; font-weight:800; }
.btn-primary{ background:#6D5EF9; color:#fff; border-color:#6D5EF9; box-shadow:0 6px 16px rgba(109,94,249,0.25); }
.btn[disabled]{ opacity:.6; cursor:not-allowed; }
.hint{ color:#6B7280; font-size:13px; }
.ratio-hint{ color:#6B7280; font-size:12px; margin-top:4px; }
.preview-title{ font-weight:800; margin-bottom:10px; }
.preview-box{
  height:320px; border:1px dashed #E5E7EB; border-radius:14px; overflow:hidden; position:relative;
  background: linear-gradient(180deg, #fafafa, #f7f7fd 70%);
}
.preview-box img{ width:100%; height:100%; object-fit:cover; display:block; }
.preview-box.with-style{ border-style:solid; }
.preview-gradient{
  position:absolute; inset:0;
  background: radial-gradient(800px 300px at 50% -10%, rgba(128,78,255,.12), transparent 60%),
              linear-gradient(180deg, #fafaff, #f6f3ff 70%);
}
.preview-badge-wrapper{
  position:absolute; 
  top:12px; 
  right:12px;
  display:flex;
  align-items:center;
  gap:6px;
}
.preview-badge{
  background:#6D5EF9; 
  color:#fff;
  padding:6px 10px; 
  border-radius:999px; 
  font-weight:800; 
  box-shadow: 0 8px 20px rgba(109,94,249,0.25);
}
.preview-dev-badge{
  background:#FEF3C7;
  color:#92400E;
  padding:4px 8px;
  border-radius:999px;
  font-size:12px;
  font-weight:800;
  border:1px solid #FCD34D;
  box-shadow: 0 4px 12px rgba(252,211,77,0.3);
  cursor:default;
}
.preview-meta{ color:#6B7280; font-size:14px; margin-top:8px; }
.artist-attribution{
  margin-top: 12px;
  padding: 12px;
  background: linear-gradient(135deg, #F3F0FF 0%, #FAF5FF 100%);
  border: 1px solid #E9D5FF;
  border-radius: 12px;
}
.artist-attribution-text{
  color: #1F2937;
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 6px;
}
.artist-attribution-text strong{
  color: #6D5EF9;
  font-weight: 800;
}
.artist-attribution-stats-row{
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.artist-attribution-stats{
  color: #6B7280;
  font-size: 13px;
  font-weight: 600;
}
.artist-reputation{
  display: inline-flex;
}
.reputation-badge{
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: #FFFFFF;
  border: 1.5px solid;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  transition: all 0.2s ease;
}
.reputation-badge:hover{
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}
.reputation-icon{
  font-size: 12px;
  line-height: 1;
}
.reputation-label{
  line-height: 1;
}
.placeholder-box{ display:flex; align-items:center; justify-content:center; }
.placeholder-img{ width:70%; height:70%; object-fit:contain; animation: gentleFloat 3s ease-in-out infinite; }
@keyframes gentleFloat { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-12px); } }
.error{ margin-top:10px; color:#8a1f1f; background:#fff7f7; border:1px solid #f2c2c2; padding:10px 12px; border-radius:10px; }
.creator-callout{
  margin-top: 20px;
  padding: 18px;
  background: linear-gradient(135deg, #F3F0FF 0%, #E9E3FF 100%);
  border-radius: 14px;
  border: 1px solid #E7E2FF;
  box-shadow: 0 4px 12px rgba(109, 94, 249, 0.08);
  animation: slideUp 0.5s ease-out;
}
@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.creator-callout-header{
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}
.creator-callout-icon{
  font-size: 28px;
  line-height: 1;
  filter: drop-shadow(0 2px 4px rgba(0,0,0,0.05));
}
.creator-callout-title{
  font-size: 18px;
  font-weight: 800;
  color: #111827;
  margin: 0;
}
.creator-callout-text{
  color: #4B5563;
  font-size: 14px;
  line-height: 1.5;
  margin: 0 0 14px 0;
}
.creator-callout-benefits{
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}
.benefit-item{
  display: flex;
  align-items: center;
  gap: 8px;
  color: #1F2937;
  font-size: 13px;
  font-weight: 600;
}
.benefit-icon{
  font-size: 16px;
  filter: drop-shadow(0 1px 2px rgba(0,0,0,0.05));
}
.benefit-text{
  color: #4B5563;
}
.creator-callout-cta{
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 12px 16px;
  background: #6D5EF9;
  color: #FFFFFF;
  font-weight: 800;
  font-size: 14px;
  text-decoration: none;
  border: none;
  border-radius: 10px;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(109, 94, 249, 0.2);
  cursor: pointer;
}
.creator-callout-cta:hover{
  background: #5B4EE8;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(109, 94, 249, 0.3);
}
    `}</style>
  );
}